import React, { useEffect, useState } from 'react';
import { ToastContainer } from 'react-toastify';
import { Badge, Spinner, Table, TextInput } from 'flowbite-react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { HiOutlineSearch } from 'react-icons/hi';

import Title from '../../../components/Title';
import Button from '../../../components/Button';
import Notification from '../../../components/Notification';
import { DEFAULT_PAGE_SIZE, fetchBatches } from './api';
import { batchDisplayId, packageProgressColor } from './helpers';
import { batchesStrings } from './strings';
import { formatMessage } from '../strings';
import { formatDate } from '../helpers';
import { BatchStatus, BatchStatusFilter } from './types';

/** Tab order: Open first (actionable), then Shipped, then "all". */
const TAB_ORDER: BatchStatus[] = ['open', 'shipped'];

const SEARCH_DEBOUNCE_MS = 300;

/** Flowbite badge color for each batch status pill. */
const STATUS_BADGE_COLOR: Record<BatchStatus, string> = {
  open: 'info',
  shipped: 'success',
};

const STATUS_TAB_LABEL: Record<BatchStatus, string> = {
  open: batchesStrings.list.tabOpen,
  shipped: batchesStrings.list.tabShipped,
};

/**
 * Batches list: Open/Shipped/All tabs, searchable/paginated table of print
 * batches, one row per batch showing its package progress toward the
 * (backend-supplied) target, order count, and dates. Mirrors
 * `printShop/index.tsx`'s exact query shape, hand-rolled tabs, and debounced
 * search — the landing screen for the Batches sub-feature and the entry
 * point into the builder (`BatchBuilder.tsx`, task_05).
 */
export default function BatchesList() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<BatchStatusFilter>('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // Debounce the search box into the query param so each keystroke does not
  // hit the backend (the api layer translates UI page → DS 0-based).
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isError, isFetching } = useQuery(
    ['print-shop-batches', activeTab, search, page],
    () =>
      fetchBatches({
        status: activeTab,
        search,
        page,
        limit: DEFAULT_PAGE_SIZE,
      }),
    { keepPreviousData: true, refetchOnWindowFocus: false },
  );

  const batches = data?.data ?? [];
  const meta = data?.meta;
  const lastPage = Math.max(1, meta?.lastPage ?? 1);

  const selectTab = (tab: BatchStatusFilter) => {
    setActiveTab(tab);
    setPage(1);
  };

  const renderTab = (tab: BatchStatusFilter) => {
    const label =
      tab === 'all' ? batchesStrings.list.tabAll : STATUS_TAB_LABEL[tab];
    const isActive = activeTab === tab;
    return (
      <button
        key={tab}
        type="button"
        data-testid={`batch-tab-${tab}`}
        onClick={() => selectTab(tab)}
        className={`flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-semibold ${
          isActive
            ? 'border-primary-500 text-primary-600'
            : 'border-transparent text-neutral-500 hover:text-neutral-800'
        }`}
      >
        {label}
      </button>
    );
  };

  return (
    <div className="pt-8 min-h-screen w-full">
      <ToastContainer />
      <div className="mb-8 flex items-center justify-between">
        <Title>{batchesStrings.list.pageTitle}</Title>
        <Button
          variant="PRIMARY"
          onClick={() => navigate('/print-shop/batches/new')}
        >
          {batchesStrings.list.newBatchButton}
        </Button>
      </div>

      <div className="shadow-md bg-white rounded-lg">
        <div
          className="flex flex-wrap items-center gap-1 border-b border-neutral-100 px-4"
          data-testid="batch-status-tabs"
        >
          {TAB_ORDER.map(renderTab)}
          {renderTab('all')}
        </div>

        <div className="px-4 py-3 border-b border-neutral-100">
          <TextInput
            data-testid="batch-search"
            icon={HiOutlineSearch}
            placeholder={batchesStrings.list.searchPlaceholder}
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            className="max-w-md"
          />
        </div>

        {isError && (
          <div className="px-4 pt-4">
            <Notification
              type="FAILURE"
              message={batchesStrings.list.loadError}
            />
          </div>
        )}

        {isLoading ? (
          <div className="px-4 py-10 flex justify-center">
            <Spinner size="lg" aria-label={batchesStrings.list.pageTitle} />
          </div>
        ) : (
          <div className="px-4 py-2 overflow-x-auto">
            <Table data-testid="print-shop-batches-table">
              <Table.Head className="whitespace-nowrap text-gray-900 capitalize border-b border-t">
                <Table.HeadCell className="px-4 py-3">
                  {batchesStrings.list.columns.batch}
                </Table.HeadCell>
                <Table.HeadCell className="px-4 py-3">
                  {batchesStrings.list.columns.status}
                </Table.HeadCell>
                <Table.HeadCell className="px-4 py-3">
                  {batchesStrings.list.columns.packages}
                </Table.HeadCell>
                <Table.HeadCell className="px-4 py-3">
                  {batchesStrings.list.columns.orders}
                </Table.HeadCell>
                <Table.HeadCell className="px-4 py-3">
                  {batchesStrings.list.columns.created}
                </Table.HeadCell>
                <Table.HeadCell className="px-4 py-3">
                  {batchesStrings.list.columns.shipped}
                </Table.HeadCell>
                <Table.HeadCell className="px-4 py-3">
                  {batchesStrings.list.columns.trackingRef}
                </Table.HeadCell>
              </Table.Head>
              <Table.Body className="divide-y whitespace-nowrap">
                {batches.map((batch) => {
                  const color = packageProgressColor(
                    batch.packageCount,
                    batch.targetPackages,
                  );
                  return (
                    <Table.Row
                      key={batch.id}
                      data-testid={`batch-row-${batch.id}`}
                      role="link"
                      tabIndex={0}
                      aria-label={batch.id}
                      className="border-b cursor-pointer hover:bg-[#EDFAFC] focus:outline-none focus:ring-2 focus:ring-primary-300"
                      onClick={() => navigate(`/print-shop/batches/${batch.id}`)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          navigate(`/print-shop/batches/${batch.id}`);
                        }
                      }}
                    >
                      <Table.Cell className="px-4 py-3 font-mono text-xs">
                        {batchDisplayId(batch.id)}
                      </Table.Cell>
                      <Table.Cell className="px-4 py-3">
                        <Badge
                          color={STATUS_BADGE_COLOR[batch.status]}
                          className="w-fit"
                        >
                          {STATUS_TAB_LABEL[batch.status]}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell className="px-4 py-3">
                        <span
                          data-testid={`batch-progress-${batch.id}`}
                          className={
                            color === 'green'
                              ? 'font-semibold text-green-600'
                              : 'font-semibold text-amber-600'
                          }
                        >
                          {formatMessage(batchesStrings.list.packageProgress, {
                            count: String(batch.packageCount),
                            target: String(batch.targetPackages),
                          })}
                        </span>
                      </Table.Cell>
                      <Table.Cell className="px-4 py-3">
                        {batch.orderCount}
                      </Table.Cell>
                      <Table.Cell className="px-4 py-3">
                        {formatDate(batch.createdAt)}
                      </Table.Cell>
                      <Table.Cell className="px-4 py-3">
                        {formatDate(batch.shippedAt)}
                      </Table.Cell>
                      <Table.Cell className="px-4 py-3 text-xs">
                        {batch.trackingRef ?? batchesStrings.list.noTracking}
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
            {!isError && batches.length === 0 && (
              <p className="text-neutral-500 text-sm px-4 py-6">
                {batchesStrings.list.empty}
              </p>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-neutral-100 px-4 py-3 text-sm text-neutral-500">
          <span>
            {formatMessage(batchesStrings.list.perPage, {
              limit: String(DEFAULT_PAGE_SIZE),
            })}
          </span>
          <div className="flex items-center gap-3">
            <Button
              variant="OUTLINED"
              disabled={page <= 1 || isFetching}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              {batchesStrings.list.prev}
            </Button>
            <span data-testid="batches-page-indicator">
              {formatMessage(batchesStrings.list.pageIndicator, {
                currentPage: String(page),
                lastPage: String(lastPage),
              })}
            </span>
            <Button
              variant="OUTLINED"
              disabled={page >= lastPage || isFetching}
              onClick={() => setPage((current) => current + 1)}
            >
              {batchesStrings.list.next}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
